import { useQuery } from '@apollo/client';
import { FileAttachmentsWidget } from '@procureai/ui-modules-mui';
import { isEmpty } from 'ramda';
import { useTranslation } from 'react-i18next';

import { FileAttachmentStatus } from '../FileAttachmentsWidget/FileAttachmentsWidget.types';
import {
  getAttachmentFragments,
  useFileAttachmentsUpdateMutation,
  FILE_ACCEPTS,
  classificationOptions,
} from '../FileAttachmentsWidget/FileAttachmentsWidget.util';
import { useNegotiationDetail } from '../Module.hooks';
import {
  TsrctAttachmentFilterInput,
  TsrctAttachmentParentInput,
  TsrctAttachmentParentVariant,
  TsrctUploadedByVariant,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types/I18nNamespace';
import { isNegotiationCaseEditable } from '@/legacy/util/negotiationCase/isNegotiationCaseEditable';

import { NEGOTIATION_CASE_SUPPLIER_FILE_ATTACHMENTS_QUERY } from './NegotiationCaseSupplierFileAttachmentsWidget.gql';

export const NegotiationCaseSupplierFileAttachmentsWidget = () => {
  const { negotiationId } = useNegotiationDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationCaseSupplierFileAttachmentsWidget' });
  const negotiationCaseParent = {
    variant: TsrctAttachmentParentVariant.NEGOTIATION_CASE,
    id: negotiationId!,
  };

  const variables: {
    parents: TsrctAttachmentParentInput[];
    filter: TsrctAttachmentFilterInput;
    negotiationCaseId: string;
  } = {
    parents: [negotiationCaseParent],
    filter: {
      uploadedByVariants: [TsrctUploadedByVariant.SUPPLIER_USER],
    },
    negotiationCaseId: negotiationId,
  };

  const { data, loading } = useQuery(NEGOTIATION_CASE_SUPPLIER_FILE_ATTACHMENTS_QUERY, {
    variables,
  });

  const { save, saving } = useFileAttachmentsUpdateMutation({
    refetchQueries: [{ query: NEGOTIATION_CASE_SUPPLIER_FILE_ATTACHMENTS_QUERY, variables }],
    parent: negotiationCaseParent,
  });

  const attachments = getAttachmentFragments(data?.tesseract.attachments);

  return (
    <>
      <FileAttachmentsWidget
        editFeatures={[]}
        title={t('title')}
        emptyStateTitle={t('empty')}
        uploadButtonLabel={t('upload.buttonLabel')}
        attachments={attachments}
        loading={loading}
        onSave={save}
        saving={saving}
        isEditable={isNegotiationCaseEditable(data?.tesseract.negotiationCase) && !loading && !isEmpty(attachments)}
        getItemStatus={(attachment) =>
          attachment.visibilities.some((v) => v.id === negotiationId)
            ? FileAttachmentStatus.VISIBLE
            : FileAttachmentStatus.HIDDEN
        }
        isItemDeletable={(attachment) => attachment.parent.id === negotiationId}
        accept={FILE_ACCEPTS}
        classificationOptions={classificationOptions}
        getItemClassification={(attachment) => attachment.classification}
      />
    </>
  );
};
